import React, { useCallback, useEffect, useMemo, useState } from "react"
import { DatePicker, Spin, Tooltip, message } from "antd"
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { Analytics } from "../../Apis/analyticsApi"
import DropdownFilter from "../../components/Common/DropdownFilter"
import { translations } from "../../utils/Translation/staticTranslation"
import SubscriptionChart from "../../components/Dashboard/SubscriptionChart"
import PlanUsageTabs from "./PlanUsageTabs"
import "../../components/PlansPricing/PlanUsage.css"

const { RangePicker } = DatePicker

// Same options, order and labels as the Dashboard's date filter, so the two
// pages read identically. Each maps to a filterType the endpoint accepts;
// "Till Date" is the backend's `alltime`, which starts at the earliest
// generation on record.
const RANGE_OPTIONS = {
  en: [
    { label: "Till Date", value: "tilldate" },
    { label: "Last 7 days", value: "last7days" },
    { label: "Last 30 days", value: "last30days" },
    { label: "Last 90 days", value: "last90days" },
    { label: "Custom Range", value: "customdate" },
  ],
  ar: [
    { label: "حتى التاريخ", value: "tilldate" },
    { label: "آخر ٧ أيام", value: "last7days" },
    { label: "آخر ٣٠ يومًا", value: "last30days" },
    { label: "آخر ٩٠ يومًا", value: "last90days" },
    { label: "نطاق مخصص", value: "customdate" },
  ],
}

// Mirrors how the Plan Distribution donut (components/Dashboard/Subscription-
// Chart) picks its colours: a fixed colour for the plans it names, otherwise the
// palette by position in the distribution. Duplicated rather than imported
// because that component derives them privately; keeping the two in step is what
// makes one plan read as one colour across the page, so if its palette changes
// this has to follow.
const DONUT_PLAN_COLORS = {
  "Free Trial": "#774bcf",
  Starter: "#318aa3",
  Basic: "#61e98e",
  Flex: "#3268DE",
  Pro: "#fb6515",
  Business: "#000000",
}
const DONUT_PALETTE = ["#774bcf", "#318aa3", "#61e98e", "#fb6515", "#000000"]

// Fallback for a plan that somehow is not in the distribution (it has no
// subscribers, so it is normally filtered out of the charts anyway).
const PLAN_ACCENT_FALLBACK = "#98A2B3"

/** #RRGGBB → rgba() at `alpha`. The panels are white, so a translucent fill
 *  reads as a lighter tint of the donut's colour rather than a different one. */
const tint = (hex, alpha) => {
  const value = String(hex || "").replace("#", "")
  if (value.length !== 6) return hex
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Bars sit on white and want to stay light; badges are small, so they need the
 *  faintest wash with the full-strength colour for their text. */
const BAR_TINT = 0.55
const BADGE_TINT = 0.14

// Below this, a "per subscriber" average is one or two people's behaviour rather
// than a property of the plan, so the average charts mute it instead of letting
// it set the scale for every other bar.
const LOW_SAMPLE_SUBSCRIBERS = 5

// Usage-health thresholds. Under-use is the thing this page exists to surface,
// so low percentages are the ones that get a warning colour.
const USAGE_BANDS = [
  { min: 60, color: "#12B76A" },
  { min: 35, color: "#F79009" },
  { min: 0, color: "#F04438" },
]

const usageColor = (percent) => {
  if (percent == null) return "#D0D5DD"
  return USAGE_BANDS.find((band) => percent >= band.min).color
}

/**
 * {plan name: colour}, reproducing the donut's own assignment for this exact
 * data so the two charts agree.
 *
 * It has to be derived from the distribution, not from the table order: the
 * donut colours by position in *its* list (busiest plan first) while the table
 * and bars are ordered by price. Mapping on plan name is what keeps them
 * aligned when those two orders diverge.
 */
const planColorsFromDistribution = (distribution) => {
  const colors = {}
  Object.keys(distribution || {}).forEach((name, index) => {
    colors[name] =
      DONUT_PLAN_COLORS[name] || DONUT_PALETTE[index % DONUT_PALETTE.length]
  })
  return colors
}

/** "Premium+" -> "P+", "Signature" -> "S" — the square badge in the first column. */
const planInitials = (name) => {
  const label = String(name || "").trim()
  if (!label) return "?"
  const plus = label.includes("+") ? "+" : ""
  return `${label[0].toUpperCase()}${plus}`
}

const formatNumber = (value) => {
  if (value == null) return "—"
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(Number(value)) ? 0 : 1,
    maximumFractionDigits: 1,
  })
}

const formatPercent = (value) => (value == null ? "—" : `${value}%`)

/** "SAR 12,000" — same convention as the Plans & Pricing screens. Halalas are
 *  dropped: these are period totals, where two decimals are noise. */
const formatCurrency = (value) =>
  value == null
    ? "—"
    : `SAR ${Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })}`

/** Horizontal meter used in both Usage % columns. */
const UsageBar = ({ percent }) => (
  <div className="plan-usage-bar-row">
    <div className="plan-usage-bar-track">
      <div
        className="plan-usage-bar-fill"
        style={{
          width: `${Math.min(Math.max(percent ?? 0, 0), 100)}%`,
          background: usageColor(percent),
        }}
      />
    </div>
    <span className="plan-usage-bar-value">{formatPercent(percent)}</span>
  </div>
)

/** Donut for the Overall Plan Usage % column. Pure SVG — no chart lib needed. */
const UsageDonut = ({ percent }) => {
  const size = 40
  const stroke = 4
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const filled = Math.min(Math.max(percent ?? 0, 0), 100)
  const color = usageColor(percent)

  return (
    <div className="plan-usage-donut-row">
      <svg width={size} height={size} className="plan-usage-donut">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F2F4F7"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(filled / 100) * circumference} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="plan-usage-donut-value" style={{ color }}>
        {formatPercent(percent)}
      </span>
    </div>
  )
}

/**
 * Vertical bar chart. Bars are sized against the largest value in the set, so
 * the shape stays readable whatever the absolute numbers are.
 *
 * `perSubscriber` marks the charts whose value is an average. Those get the
 * sample size on every bar and mute the ones computed over too few subscribers
 * to mean anything — without it a plan with a single subscriber plots that one
 * person's usage as a towering bar beside averages over dozens, and nothing on
 * the chart says so. The table already carries the subscriber counts; the chart
 * was the only place reading it without them.
 */
const BarChart = ({
  rows,
  valueKey,
  axisTicks,
  formatValue = formatNumber,
  perSubscriber = false,
  planColors = {},
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.planUsagePage || {}

  const max = Math.max(...rows.map((row) => Number(row[valueKey]) || 0), 1)
  const ticks = axisTicks ?? 3
  const scaleTop = Math.ceil(max / ticks) * ticks || ticks

  const gridValues = Array.from(
    { length: ticks + 1 },
    (_, i) => (scaleTop / ticks) * (ticks - i)
  )

  const isLowSample = (row) =>
    perSubscriber && Number(row.subscribers) < LOW_SAMPLE_SUBSCRIBERS

  return (
    <div className="plan-usage-chart">
      <div className="plan-usage-chart-axis">
        {gridValues.map((value) => (
          <span key={value} className="plan-usage-chart-tick">
            {formatNumber(value)}
          </span>
        ))}
      </div>
      <div className="plan-usage-chart-plot">
        <div className="plan-usage-chart-grid">
          {gridValues.map((value) => (
            <div key={value} className="plan-usage-chart-gridline" />
          ))}
        </div>
        <div className="plan-usage-chart-bars">
          {rows.map((row) => {
            const value = Number(row[valueKey]) || 0
            const lowSample = isLowSample(row)
            const accent = planColors[row.plan_name] || PLAN_ACCENT_FALLBACK
            return (
              <div className="plan-usage-chart-column" key={row.plan_id}>
                <span
                  className={`plan-usage-chart-label ${
                    lowSample ? "plan-usage-chart-label-muted" : ""
                  }`}
                >
                  {formatValue(value)}
                </span>
                <Tooltip
                  title={
                    lowSample
                      ? (
                          t.low_sample_tooltip ||
                          "Averaged over only {count} subscriber(s) — not a meaningful plan average."
                        ).replace("{count}", row.subscribers)
                      : null
                  }
                >
                  <div
                    className={`plan-usage-chart-bar ${
                      lowSample ? "plan-usage-chart-bar-muted" : ""
                    }`}
                    style={{
                      height: `${(value / scaleTop) * 100}%`,
                      background: lowSample
                        ? "#D0D5DD"
                        : tint(accent, BAR_TINT),
                    }}
                  />
                </Tooltip>
              </div>
            )
          })}
        </div>
      </div>
      <div className="plan-usage-chart-names">
        {rows.map((row) => (
          <span key={row.plan_id} className="plan-usage-chart-name">
            {row.plan_name}
            {/* Same reason as the table: distinguish the yearly tier from the
                monthly one that shares its name. */}
            {row.duration_type === "Yearly" ? (
              <span className="plan-usage-chart-name-meta"> /yr</span>
            ) : null}
            {/* The denominator, at the point of reading. */}
            <span
              className={`plan-usage-chart-name-count ${
                isLowSample(row) ? "plan-usage-chart-name-count-low" : ""
              }`}
            >
              {formatNumber(row.subscribers)}{" "}
              {Number(row.subscribers) === 1
                ? t.subscriber_singular || "subscriber"
                : t.subscribers_lower || "subscribers"}
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

/** Shown under an average chart when at least one plan's average rests on too
 *  few subscribers to read as a plan-level figure. */
const LowSampleNote = ({ t }) => (
  <p className="plan-usage-low-sample-note">
    <InfoCircleOutlined />
    <span>
      {(
        t.low_sample_note ||
        "Greyed bars are averaged over fewer than {count} subscribers, so they reflect individual behaviour rather than the plan."
      ).replace("{count}", LOW_SAMPLE_SUBSCRIBERS)}
    </span>
  </p>
)

const SummaryCard = ({ icon, label, value, caption, tint }) => (
  <div className="plan-usage-summary-card">
    <div className="plan-usage-summary-icon" style={{ background: tint }}>
      {icon}
    </div>
    <div className="plan-usage-summary-body">
      <span className="plan-usage-summary-label">{label}</span>
      <span className="plan-usage-summary-value">{value}</span>
      <span className="plan-usage-summary-caption">{caption}</span>
    </div>
  </div>
)

const PlanUsageStatistics = () => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.planUsagePage || {}

  const options = RANGE_OPTIONS[lang === "ar" ? "ar" : "en"]

  // Matches the Dashboard's default so both pages open on the same window.
  const [filterType, setFilterType] = useState("tilldate")
  const [range, setRange] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const requestParams = useMemo(() => {
    if (filterType !== "customdate") {
      // "Till Date" is the endpoint's `alltime`; the rest pass straight through.
      return { filterType: filterType === "tilldate" ? "alltime" : filterType }
    }
    return {
      filterType: "customdate",
      startDate: range?.[0]?.format("YYYY-MM-DD"),
      endDate: range?.[1]?.format("YYYY-MM-DD"),
    }
  }, [filterType, range])

  // Custom Range shows the picker first and only fetches once both ends are
  // chosen, so selecting it does not fire a request with null dates.
  const paramsReady =
    requestParams.filterType !== "customdate" ||
    Boolean(requestParams.startDate && requestParams.endDate)

  const fetchStats = useCallback(async () => {
    if (!paramsReady) return
    setLoading(true)
    try {
      const response = await Analytics.getPlanUsageStats(requestParams)
      setData(response?.data || null)
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          t.error_fetching ||
          "Could not load plan usage statistics."
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [requestParams, paramsReady, t.error_fetching])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await Analytics.exportPlanUsageStats(requestParams)
      const blob = response?.data
      if (!(blob instanceof Blob)) {
        message.error(t.error_export || "Could not export the CSV.")
        return
      }
      const disposition = response?.headers?.["content-disposition"]
      const match = disposition?.match(
        /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
      )
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = match?.[1]?.trim() || "plan_usage.csv"
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error(t.error_export || "Could not export the CSV.")
    } finally {
      setExporting(false)
    }
  }

  const summary = data?.summary
  const plans = data?.plans || []

  // SubscriptionChart reads `plan_usage` off one object. Memoised because its
  // effect keys on the object identity — a fresh literal every render would tear
  // down and rebuild the canvas on any state change.
  const distributionData = useMemo(
    () => ({ plan_usage: data?.plan_distribution || {} }),
    [data?.plan_distribution]
  )

  // Keyed on plan name so the bars, the table badges and the donut all give a
  // plan the same colour, whatever order each of them lists plans in.
  const planColors = useMemo(
    () => planColorsFromDistribution(data?.plan_distribution),
    [data?.plan_distribution]
  )

  // Plans nobody is on would render as four empty bars and a 0% row, which
  // reads as broken rather than as "no subscribers".
  const activePlans = plans.filter((plan) => plan.subscribers > 0)

  const hasLowSamplePlan = activePlans.some(
    (plan) => Number(plan.subscribers) < LOW_SAMPLE_SUBSCRIBERS
  )

  return (
    <div className="plan-usage-page">
      <div className="plan-usage-header">
        <div>
          <h1 className="plan-usage-title">
            {t.title || "Plan Usage Statistics"}
          </h1>
          <p className="plan-usage-subtitle">
            {t.subtitle || "Average usage per subscriber by plan type"}
          </p>
        </div>
        <div className="plan-usage-actions">
          <PlanUsageTabs />
          {filterType === "customdate" && (
            <RangePicker
              value={range}
              onChange={(value) => setRange(value || null)}
              allowClear={false}
              // No future dates: there is no usage to report past today.
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              format="MMM D, YYYY"
              className="plan-usage-rangepicker"
            />
          )}
          {/* DropdownFilter's own .select-dropdown already draws the bordered
              pill (App.css), so it is used bare here exactly as on the
              Dashboard — wrapping it would double the border. */}
          <DropdownFilter
            // Wide enough for the longest label ("Custom Range") on one line;
            // the Dashboard's 182px only ever has to fit "Till Date".
            width="205px"
            label={options[0].label}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={options}
            selectedCategory={filterType}
            onSelect={setFilterType}
          />
          <button
            type="button"
            className="plan-usage-export"
            onClick={handleExport}
            disabled={exporting || loading || !plans.length}
          >
            <DownloadOutlined />
            <span>{t.export || "Export"}</span>
          </button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="plan-usage-summary-grid">
          <SummaryCard
            tint="#F5EFE3"
            icon={<span className="plan-usage-emoji">👥</span>}
            label={t.total_subscribers || "Total Subscribers"}
            value={formatNumber(summary?.total_subscribers ?? 0)}
            caption={t.across_all_plans || "Across all plans"}
          />
          <SummaryCard
            tint="#E7F5EC"
            icon={<span className="plan-usage-emoji">📈</span>}
            label={t.avg_images || "Avg. Images Generated"}
            value={formatNumber(summary?.avg_images_generated ?? 0)}
            caption={t.per_subscriber || "Per subscriber"}
          />
          <SummaryCard
            tint="#EFEAFB"
            icon={<span className="plan-usage-emoji">🔎</span>}
            label={t.avg_finder || "Avg. Finder Usage"}
            value={formatNumber(summary?.avg_finder_usage ?? 0)}
            caption={t.per_subscriber || "Per subscriber"}
          />
          <SummaryCard
            tint="#FDEEE1"
            icon={<span className="plan-usage-emoji">🥧</span>}
            label={t.overall_usage || "Overall Plan Usage"}
            value={formatPercent(summary?.overall_usage_percent)}
            caption={t.of_included || "Of included allowance"}
          />
        </div>

        <section className="plan-usage-panel">
          <h2 className="plan-usage-panel-title">
            {t.average_by_plan || "Average Usage by Plan"}
          </h2>

          <div className="plan-usage-table-scroll">
            <table className="plan-usage-table">
              <thead>
                <tr className="plan-usage-group-row">
                  <th rowSpan={2} className="plan-usage-th-plan">
                    {t.plan || "Plan"}
                  </th>
                  <th rowSpan={2}>{t.subscribers || "Subscribers"}</th>
                  {/* Not a per-subscriber figure like everything to its right,
                      so it sits outside both groups. */}
                  <th rowSpan={2}>
                    <span className="plan-usage-th-with-info">
                      {t.amount_spent || "Amount Spent"}
                      <Tooltip
                        title={
                          t.amount_spent_tooltip ||
                          "Total paid for this plan in the period. Counts every payment, so a subscriber who renewed or upgraded contributes more than once."
                        }
                      >
                        <InfoCircleOutlined className="plan-usage-info" />
                      </Tooltip>
                    </span>
                  </th>
                  <th colSpan={4} className="plan-usage-group">
                    {t.images || "Images"}
                  </th>
                  <th colSpan={4} className="plan-usage-group">
                    {t.furniture_finder || "Furniture Finder"}
                  </th>
                  <th rowSpan={2}>
                    <span className="plan-usage-th-with-info">
                      {t.overall_plan_usage || "Overall Plan Usage %"}
                      <Tooltip
                        title={
                          t.overall_tooltip ||
                          "The average of the Images and Furniture Finder usage percentages. Both allowances count equally, whatever their size."
                        }
                      >
                        <InfoCircleOutlined className="plan-usage-info" />
                      </Tooltip>
                    </span>
                  </th>
                </tr>
                {/* Total first, then the average derived from it — the raw
                    count is the primary figure and the per-head number reads as
                    a breakdown of it. */}
                <tr className="plan-usage-sub-row">
                  <th>{t.total_images_generated || "Total Images Generated"}</th>
                  <th>{t.avg_images_generated || "Avg. Images Generated"}</th>
                  <th>{t.images_included || "Images Included"}</th>
                  <th>{t.usage_percent || "Usage %"}</th>
                  <th>{t.total_finder_usage || "Total Finder Usage"}</th>
                  <th>{t.avg_finder_usage || "Avg. Finder Usage"}</th>
                  <th>{t.finder_included || "Finder Included"}</th>
                  <th>{t.usage_percent || "Usage %"}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  // Same colour the donut and the bars give this plan.
                  const accent =
                    planColors[plan.plan_name] || PLAN_ACCENT_FALLBACK
                  return (
                  <tr key={plan.plan_id}>
                    <td className="plan-usage-td-plan">
                      <span
                        className="plan-usage-badge"
                        style={{
                          background: tint(accent, BADGE_TINT),
                          color: accent,
                        }}
                      >
                        {planInitials(plan.plan_name)}
                      </span>
                      <span className="plan-usage-plan-cell">
                        <span className="plan-usage-plan-name">
                          {lang === "ar"
                            ? plan.plan_name_arabic || plan.plan_name
                            : plan.plan_name}
                        </span>
                        {/* Monthly and yearly tiers share a name ("Advanced"
                            exists as both), so the billing period has to be on
                            the row or two lines look like duplicates. */}
                        {plan.duration_type ? (
                          <span className="plan-usage-plan-meta">
                            {plan.duration_type}
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td>{formatNumber(plan.subscribers)}</td>
                    <td>
                      <span className="plan-usage-metric">
                        {formatCurrency(plan.amount_spent)}
                      </span>
                    </td>
                    <td>
                      <span className="plan-usage-metric">
                        {formatNumber(plan.total_images_generated)}
                      </span>
                      <span className="plan-usage-metric-caption">
                        {t.all_subscribers_lower || "all subscribers"}
                      </span>
                    </td>
                    <td>
                      <span className="plan-usage-metric">
                        {formatNumber(plan.avg_images_generated)}
                      </span>
                      <span className="plan-usage-metric-caption">
                        {t.per_subscriber_lower || "per subscriber"}
                      </span>
                    </td>
                    <td>{formatNumber(plan.images_included)}</td>
                    <td>
                      <UsageBar percent={plan.images_usage_percent} />
                    </td>
                    <td>
                      <span className="plan-usage-metric">
                        {formatNumber(plan.total_finder_usage)}
                      </span>
                      <span className="plan-usage-metric-caption">
                        {t.all_subscribers_lower || "all subscribers"}
                      </span>
                    </td>
                    <td>
                      <span className="plan-usage-metric">
                        {formatNumber(plan.avg_finder_usage)}
                      </span>
                      <span className="plan-usage-metric-caption">
                        {t.per_subscriber_lower || "per subscriber"}
                      </span>
                    </td>
                    <td>{formatNumber(plan.finder_included)}</td>
                    <td>
                      <UsageBar percent={plan.finder_usage_percent} />
                    </td>
                    <td>
                      <UsageDonut percent={plan.overall_usage_percent} />
                    </td>
                  </tr>
                  )
                })}
                {!loading && !plans.length && (
                  <tr>
                    <td colSpan={12} className="plan-usage-empty">
                      {t.no_data ||
                        "No plan usage for this period."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {activePlans.length > 0 && (
          <div className="plan-usage-charts">
            {/* Who is on what, and what it earns — the two plan-level questions,
                paired. */}
            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.plan_distribution || "Plan Distribution"}
              </h2>
              {/* Derived from the same rows as the table above, so the slices
                  always add up to its Subscribers column. */}
              <div className="plan-usage-donut-panel">
                <SubscriptionChart data={distributionData} />
              </div>
            </section>

            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.chart_amount_spent || "Amount Spent per Plan"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.in_period || "in the selected period"}
              </p>
              <BarChart
                rows={activePlans}
                valueKey="amount_spent"
                formatValue={formatCurrency}
                planColors={planColors}
              />
            </section>

            {/* Totals before averages, matching the table. Read together they
                separate "each subscriber uses this a lot" from "this plan drives
                most of our volume" — a small plan can lead on average and still
                be a rounding error in total. */}
            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.chart_total_images || "Total Images Generated per Plan"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.all_subscribers_lower || "all subscribers"}
              </p>
              <BarChart
                rows={activePlans}
                valueKey="total_images_generated"
                planColors={planColors}
              />
            </section>

            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.chart_total_finder || "Total Furniture Finder Usage per Plan"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.all_subscribers_lower || "all subscribers"}
              </p>
              <BarChart
                rows={activePlans}
                valueKey="total_finder_usage"
                planColors={planColors}
              />
            </section>

            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.chart_images || "Average Images Generated per Subscriber"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.per_subscriber_lower || "per subscriber"}
              </p>
              <BarChart
                rows={activePlans}
                valueKey="avg_images_generated"
                perSubscriber
                planColors={planColors}
              />
              {hasLowSamplePlan && <LowSampleNote t={t} />}
            </section>

            <section className="plan-usage-panel">
              <h2 className="plan-usage-panel-title">
                {t.chart_finder ||
                  "Average Furniture Finder Usage per Subscriber"}
              </h2>
              <p className="plan-usage-panel-subtitle">
                {t.per_subscriber_lower || "per subscriber"}
              </p>
              <BarChart
                rows={activePlans}
                valueKey="avg_finder_usage"
                perSubscriber
                planColors={planColors}
              />
              {hasLowSamplePlan && <LowSampleNote t={t} />}
            </section>
          </div>
        )}

      </Spin>
    </div>
  )
}

export default PlanUsageStatistics
